import { Injectable, computed, inject, signal } from '@angular/core';
import {
  Subscription,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  tap,
  timer
} from 'rxjs';

import { AuthFacade } from '@cpd-app/state/auth/auth.facade';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth/cognito-auth.facade';

import {
  DismissNotificationGQL,
  GetNotificationsGQL,
  NotificationFieldsFragment
} from '@cpd-app/generated';

/** How long the drawer spins before it stops waiting on screen for a session. */
const STALL_MS = 3000;

/**
 * PWS 4.3.4. The shell's notification feed.
 *
 * Held as a signal rather than re-queried per surface: the drawer is part of
 * the shell and is opened from every page, so a fetch per page would be a fetch
 * per navigation for a list that rarely changes.
 *
 * A dismiss is applied locally as soon as the server confirms it, rather than
 * refetching the list. The mutation returns the row it changed, which is enough
 * to reconcile, and a refetch would flash the whole drawer to remove one line.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  readonly #notifications = signal<NotificationFieldsFragment[]>([]);
  readonly #hasLoaded = signal(false);
  readonly #loadFailed = signal(false);

  public readonly notifications = this.#notifications.asReadonly();
  public readonly hasLoaded = this.#hasLoaded.asReadonly();
  /** Distinct from having no alerts: a member with unread replies should not be told there are none. */
  public readonly loadFailed = this.#loadFailed.asReadonly();
  public readonly unreadCount = computed(() => this.#notifications().length);

  /**
   * L9 (live test 30 Sep). The feed is read with the member's token. A guest
   * holds a session token too, so "a token is there" is not enough: a sign-in
   * after load (a magic link, no reload) turns the session authenticated
   * while the guest token is still in place, and a read sent then comes back
   * "Forbidden resource". The feed is read again whenever the signed-in token
   * changes, so the read that counts is the one sent with the member's token.
   */
  private readonly authFacade = inject(AuthFacade);
  private readonly cognitoAuthFacade = inject(CognitoAuthFacade);

  readonly #memberToken$ = combineLatest([
    this.cognitoAuthFacade.isAuthenticated$,
    this.authFacade.authToken$
  ]).pipe(map(([signedIn, token]) => (signedIn && token ? token : null)));

  /** The signed-in token as last seen, or null while signed out or unsure. */
  #memberToken: string | null = null;
  #watching?: Subscription;
  /** The one read in flight, so asks that overlap send one request. */
  #inFlight?: Subscription;
  #stall?: Subscription;

  constructor(
    private getNotificationsGQL: GetNotificationsGQL,
    private dismissNotificationGQL: DismissNotificationGQL
  ) {}

  /**
   * Reads the feed each time the member's token lands or changes, for the
   * life of the app. The shell starts this at boot so the bell's count is in
   * place before the drawer is opened; calling it again does nothing.
   *
   * Returns true when this call started the watch (and so has already asked,
   * if the session is ready).
   */
  public watchSession(): boolean {
    if (this.#watching) {
      return false;
    }

    this.#watching = this.#memberToken$
      .pipe(
        tap((token) => (this.#memberToken = token)),
        filter((token): token is string => !!token),
        distinctUntilChanged()
      )
      .subscribe(() => {
        // A newer token supersedes a read sent with the old one.
        this.#inFlight?.unsubscribe();
        this.#request();
      });

    return true;
  }

  /**
   * Safe to call on every drawer open. It asks only when nothing has loaded
   * yet or the last read failed, so opening and closing the drawer does not
   * hammer the feed; `refresh` is the way to ask again on purpose.
   */
  public load(): void {
    if ((this.#hasLoaded() && !this.#loadFailed()) || this.#inFlight) {
      return;
    }

    this.refresh();
  }

  public refresh(): void {
    this.#loadFailed.set(false);

    if (this.watchSession()) {
      if (!this.#memberToken) {
        this.#stallAfter();
      }
      return;
    }

    if (this.#memberToken) {
      this.#request();
      return;
    }

    // No session yet: the watch reads the feed when the token lands.
    this.#stallAfter();
  }

  /**
   * Never a spinner that does not resolve. With no session after 3s, the
   * drawer stops spinning and says the feed did not load, but no request goes
   * out: one sent now could only come back Forbidden. The watch still reads
   * the feed the moment the token lands, and that replaces this state.
   */
  #stallAfter(): void {
    if (this.#stall) {
      return;
    }

    this.#stall = timer(STALL_MS).subscribe(() => {
      this.#stall = undefined;
      if (this.#inFlight || this.#memberToken) {
        return;
      }
      this.#loadFailed.set(true);
      this.#hasLoaded.set(true);
      // A new reference, as a read would give: the drawer's retry treats a
      // changed list as the answer arriving.
      this.#notifications.update((rows) => [...rows]);
    });
  }

  #request(): void {
    if (this.#inFlight) {
      return;
    }

    const inFlight = new Subscription();
    this.#inFlight = inFlight;

    inFlight.add(
      this.getNotificationsGQL
        .fetch({ includeDismissed: false }, { fetchPolicy: 'network-only' })
        .pipe(
          map((result) => ({
            rows: result?.data?.notifications ?? [],
            failed: false
          })),
          // A feed that will not load must not break the page it hangs off,
          // but it must not read as "nothing needs you" either: a member with
          // an unread counselor reply would be told there are none.
          catchError(() => of({ rows: [], failed: true })),
          tap(({ rows, failed }) => {
            this.#stall?.unsubscribe();
            this.#stall = undefined;
            this.#loadFailed.set(failed);
            this.#notifications.set(rows);
            this.#hasLoaded.set(true);
          }),
          // Cleared by the read that set it, so a read that answers
          // synchronously, or one superseded by a newer token, leaves the
          // slot free.
          finalize(() => {
            if (this.#inFlight === inFlight) {
              this.#inFlight = undefined;
            }
          })
        )
        .subscribe()
    );
  }

  public dismiss(id: string): void {
    this.dismissNotificationGQL
      .mutate({ id })
      .pipe(
        map((result) => result?.data?.dismissNotification?.success ?? false),
        catchError(() => of(false)),
        tap((success) => {
          // The row stays put when the server refused. Removing it locally
          // would tell the member it is gone until they reload and find it
          // back.
          if (success) {
            this.#notifications.update((rows) =>
              rows.filter((row) => row.id !== id)
            );
          }
        })
      )
      .subscribe();
  }
}
